"use client";

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { FileText, FileSignature } from "lucide-react";
import PlanningSignoffDialog, { PlanningSignoffTokens } from "../planning/PlanningSignoffDialog";
import { useAuth } from "@/contexts/AuthContext";
import { loadMiscSignoffs, getSignoffEntriesForCard, type MiscSignoffEntry } from "@/utils/misc-signoff-utils";
import { useEngagement } from "@/contexts/EngagementContext";
import { apiService } from "@/services/api-service";
import { formatCurrency } from "../../lib/textron-data-processor";
import { getCompanyName } from "../../lib/textron-data-processor";
import { DocumentManager } from "../planning/DocumentManager";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Label } from "../ui/label";
import { SHAREPOINT_FY_YEAR } from "@/constants/sharepoint-config";

interface EvaluateAuditResultsLandingProps {
  onSelect?: (section: string) => void;
}

interface Misstatement {
  id: string;
  type: string;
  workspace: string;
  amount: number;
}

interface QuestionResponse {
  selectedOption?: string;
  textResponse?: { html: string; plainText: string };
  documents?: string[];
  lastUpdated?: string;
  companyName?: string;
}

interface QuestionState {
  id: string;
  selectedOption: string;
  textResponse: { html: string; plainText: string } | null;
  documents: any[];
}

const WORKSPACE_NAME = "Evaluate Audit Results Including Misstatements";

export default function EvaluateAuditResultsLanding({
  onSelect,
}: EvaluateAuditResultsLandingProps) {
  const { user } = useAuth();
  const { currentEngagement } = useEngagement();
  const [userEngagementRole, setUserEngagementRole] = useState<string | null>(null);
  const [signoffDialogOpen, setSignoffDialogOpen] = useState(false);
  const [cardSignoffs, setCardSignoffs] = useState<MiscSignoffEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [companyName, setCompanyName] = useState<string>("");
  const [misstatements, setMisstatements] = useState<Misstatement[]>([]);
  const [materialityData, setMaterialityData] = useState({
    planningMateriality: 0,
    performanceMateriality: 0,
    clearlyTrivialThreshold: 0,
  });
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [hasExistingFile, setHasExistingFile] = useState(false);
  const [dirtyQuestions, setDirtyQuestions] = useState<Set<string>>(new Set());
  
  // Question states
  const [questionStates, setQuestionStates] = useState<Record<string, QuestionState>>({});

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");


        await Promise.all([
          loadMisstatements(),
          loadMateriality(name),
          loadResponses(name),
        ]);
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const loadResponses = async (companyName: string) => {
    try {
      if ((window as any).sharePointAPI?.downloadMisstatementJson) {
        const res = await (window as any).sharePointAPI.downloadMisstatementJson();
        if (res.success && res.data) {
          const librariesMisstatement = res.data.Libraries_Misstatement || res.data.libraries_Misstatement || {};
          const responses = librariesMisstatement.responses || {};
          
          // Initialize question states
          const questionIds = [
            "MISST-01", "MISST-02", "MISST-03", "MISST-04", "MISST-05",
            "MISST-06", "MISST-07", "MISST-08", "MISST-09"
          ];
          
          const initialStates: Record<string, QuestionState> = {};
          questionIds.forEach((qId) => {
            const response = responses[qId] as QuestionResponse | undefined;
            initialStates[qId] = {
              id: qId,
              selectedOption: response?.selectedOption || "",
              textResponse: response?.textResponse || null,
              documents: response?.documents || [],
            };
          });
          
          setQuestionStates(initialStates);
          setHasExistingFile(Object.keys(responses).length > 0);
        } else {
          // Initialize empty states if no file exists
          const questionIds = [
            "MISST-01", "MISST-02", "MISST-03", "MISST-04", "MISST-05",
            "MISST-06", "MISST-07", "MISST-08", "MISST-09"
          ];
          const initialStates: Record<string, QuestionState> = {};
          questionIds.forEach((qId) => {
            initialStates[qId] = {
              id: qId,
              selectedOption: "",
              textResponse: null,
              documents: [],
            };
          });
          setQuestionStates(initialStates);
        }
      }
    } catch (error) {
      console.error("Error loading responses:", error);
    }
  };

  const handleQuestionOptionChange = (questionId: string, option: string) => {
    setQuestionStates((prev) => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        selectedOption: option,
      },
    }));
    setDirtyQuestions((prev) => new Set(prev).add(questionId));
  };

  const handleQuestionDocumentsChange = (questionId: string, documents: any[]) => {
    // Sanitize documents to ensure only serializable data is stored
    const sanitizedDocuments = documents.map((doc: any) => {
      if (typeof doc === 'string') {
        return { name: doc, type: 'linked' };
      }
      if (doc && typeof doc === 'object') {
        // Only keep serializable properties
        return {
          id: doc.id || `doc_${doc.name || Math.random()}`,
          type: doc.type || 'linked',
          name: doc.name || doc.id || '',
          ...(doc.url && { url: doc.url }),
          ...(doc.content && typeof doc.content === 'string' && { content: doc.content }),
        };
      }
      return { name: String(doc), type: 'linked' };
    });
    
    setQuestionStates((prev) => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        documents: sanitizedDocuments,
      },
    }));
    setDirtyQuestions((prev) => new Set(prev).add(questionId));
  };

  const handleQuestionTextResponseChange = (questionId: string, textResponse: { html: string; plainText: string } | null) => {
    setQuestionStates((prev) => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        textResponse,
      },
    }));
    setDirtyQuestions((prev) => new Set(prev).add(questionId));
  };

  // Helper function to sanitize data for JSON serialization
  const sanitizeForJSON = (obj: any): any => {
    if (obj === null || obj === undefined) {
      return obj;
    }
    
    if (typeof obj !== 'object') {
      return obj;
    }
    
    if (Array.isArray(obj)) {
      return obj.map(item => sanitizeForJSON(item));
    }
    
    // Handle Date objects
    if (obj instanceof Date) {
      return obj.toISOString();
    }
    
    // Create a new object with only serializable properties
    const sanitized: any = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const value = obj[key];
        // Skip functions, symbols, and other non-serializable types
        if (typeof value === 'function' || typeof value === 'symbol') {
          continue;
        }
        // Recursively sanitize nested objects
        sanitized[key] = sanitizeForJSON(value);
      }
    }
    return sanitized;
  };

  const handleSave = useCallback(async () => {
    if (!companyName) {
      setSaveError("Company name is required");
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      // Load existing file
      let existingFileData: any = null;
      if ((window as any).sharePointAPI?.downloadMisstatementJson) {
        const res = await (window as any).sharePointAPI.downloadMisstatementJson();
        if (res.success && res.data) {
          existingFileData = res.data;
        }
      }

      // Initialize file structure if it doesn't exist
      if (!existingFileData) {
        existingFileData = {};
      }

      // Get or create Libraries_Misstatement structure
      if (!existingFileData.Libraries_Misstatement && !existingFileData.libraries_Misstatement) {
        existingFileData.Libraries_Misstatement = {
          misstatements: {},
          responses: {},
        };
      }

      const librariesMisstatement = existingFileData.Libraries_Misstatement || existingFileData.libraries_Misstatement;
      
      // Ensure misstatements and responses exist
      if (!librariesMisstatement.misstatements) {
        librariesMisstatement.misstatements = {};
      }
      // Ensure responses is an object (not an array)
      if (!librariesMisstatement.responses) {
        librariesMisstatement.responses = {};
      } else if (Array.isArray(librariesMisstatement.responses)) {
        // Convert array to object if it was initialized as an array
        librariesMisstatement.responses = {};
      }
      
      // Prepare responses payload
      const responses: Record<string, QuestionResponse> = {};
      
      Object.entries(questionStates).forEach(([qId, qState]) => {
        if (!dirtyQuestions.has(qId)) return;

        const textboxFromLocalStorage = localStorage.getItem(`textbox_ConcludingProcedures_EARM_${qId}`);
        const hasTextboxContent = textboxFromLocalStorage && textboxFromLocalStorage.trim().length > 0;

        const fileDocuments = (qState.documents || []).filter((d: any) => d.type !== 'textbox');
        const documentNames = fileDocuments.map((d: any) => {
          // Ensure we only extract string names, not objects
          if (typeof d === 'string') return d;
          if (d && typeof d === 'object' && d.name) return d.name;
          return String(d);
        });

        let textResponse = { html: "", plainText: "" };
        if (hasTextboxContent) {
          textResponse = {
            html: textboxFromLocalStorage,
            plainText: textboxFromLocalStorage.replace(/<[^>]*>/g, '')
          };
        } else if (qState.textResponse) {
          textResponse = qState.textResponse;
        }

        responses[qId] = {
          selectedOption: qState.selectedOption || "",
          textResponse: textResponse,
          documents: documentNames,
          lastUpdated: new Date().toISOString(),
          companyName: companyName,
        };
      });

      // Merge responses into existing data structure
      Object.entries(responses).forEach(([qId, value]) => {
        librariesMisstatement.responses[qId] = value;
      });

      // Preserve the entire file structure with proper casing
      // Keep any other top-level properties that might exist
      const finalData = {
        ...existingFileData,
        Libraries_Misstatement: {
          misstatements: librariesMisstatement.misstatements || {},
          responses: librariesMisstatement.responses || {},
        }
      };
      
      // Remove any lowercase variant if it exists
      if (finalData.libraries_Misstatement) {
        delete finalData.libraries_Misstatement;
      }

      // Sanitize the data before stringifying to remove any non-serializable properties
      const sanitizedData = sanitizeForJSON(finalData);
      const jsonString = JSON.stringify(sanitizedData, null, 4);
      console.log("[EvaluateAuditResults] Saving responses:", {
        responsesCount: Object.keys(responses).length,
        questionIds: Object.keys(responses),
        finalDataStructure: Object.keys(finalData),
        librariesMisstatementKeys: Object.keys(librariesMisstatement),
      });
      
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

      const uploadResult = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: base64Content,
        fileName: "Libraries_Misstatement.json",
        referenceValue: "Evaluate Audit Results Including Misstatements Responses",
        folderName: "juggernaut",
        fyYear: String(SHAREPOINT_FY_YEAR),
        sectionList: ["ConcludingProcedures_EARM_Responses"],
      });

      console.log("[EvaluateAuditResults] Upload result:", uploadResult);

      if (!uploadResult || !uploadResult.success) {
        throw new Error(uploadResult?.error || "Failed to save responses");
      }

      setHasExistingFile(true);
      setDirtyQuestions(new Set());
      setIsEditMode(false);
      
      // Reload responses to reflect saved state
      if (companyName) {
        await loadResponses(companyName);
      }
    } catch (error: any) {
      console.error("Error saving responses:", error);
      setSaveError(error?.message || "Failed to save responses");
    } finally {
      setIsSaving(false);
    }
  }, [companyName, questionStates, dirtyQuestions]);

  const saveButtonLabel = useMemo(() => {
    if (isSaving) return "Saving...";
    if (saveError) return "Save (Retry)";
    if (hasExistingFile) return "Save Changes";
    return "Save";
  }, [hasExistingFile, isSaving, saveError]);

  const loadMateriality = async (companyName: string) => {
    try {
      const planningAPI = (window as any).planningAPI;
      if (!planningAPI?.loadResponses) return;

      const sharePointResponses = await planningAPI.loadResponses({
        companyName,
        sectionKey: "materiality",
      });

      if (!sharePointResponses?.success || !sharePointResponses.data) return;

      const responses = sharePointResponses.data.responses || sharePointResponses.data;
      const m9Response = responses["M-9"];
      const useRevisedMateriality = m9Response?.subAnswer === "Yes";

      let planningMateriality = 0;
      let performanceMateriality = 0;
      let clearlyTrivialThreshold = 0;

      if (useRevisedMateriality) {
        const revisedMaterialityResponse = responses["revisedMateriality"];
        if (revisedMaterialityResponse) {
          planningMateriality = parseFloat(revisedMaterialityResponse.determinedMateriality || "0") || 0;
          performanceMateriality = parseFloat(revisedMaterialityResponse.performanceMateriality || "0") || 0;
          clearlyTrivialThreshold = parseFloat(revisedMaterialityResponse.trivialThreshold || "0") || 0;
        }
      } else {
        const m3Response = responses["M-3"];
        if (m3Response?.textResponse) {
          const textValue = typeof m3Response.textResponse === "string"
            ? m3Response.textResponse
            : m3Response.textResponse.plainText || m3Response.textResponse.html || "0";
          planningMateriality = parseFloat(textValue) || 0;
        }

        const m5Response = responses["M-5"];
        if (m5Response?.overridePerformanceMateriality !== undefined) {
          performanceMateriality = parseFloat(m5Response.overridePerformanceMateriality.toString()) || 0;
        }

        const m8Response = responses["M-8"];
        if (m8Response?.textResponse) {
          const textValue = typeof m8Response.textResponse === "string"
            ? m8Response.textResponse
            : m8Response.textResponse.plainText || m8Response.textResponse.html || "0";
          clearlyTrivialThreshold = parseFloat(textValue) || 0;
        }
      }

      setMaterialityData({
        planningMateriality,
        performanceMateriality,
        clearlyTrivialThreshold,
      });
    } catch (error) {
      console.error("Error loading materiality:", error);
    }
  };

  const loadMisstatements = async () => {
    try {
      if ((window as any).sharePointAPI?.downloadMisstatementJson) {
        const res = await (window as any).sharePointAPI.downloadMisstatementJson();
        if (res.success && res.data) {
          const librariesMisstatement = res.data.Libraries_Misstatement || res.data.libraries_Misstatement || {};
          const misstatementsObj = librariesMisstatement.misstatements || {};
          const items: Misstatement[] = Object.entries(misstatementsObj).map(([id, value]: [string, any]) => {
            // Get the misstatement amount from the type-specific field
            // Structure: value.factual.misstatement, value.judgmental.misstatement, or value.projected.misstatement
            const type = value.type || "Factual";
            const typeKey = type.toLowerCase(); // "factual", "judgmental", or "projected"
            const typeData = value[typeKey] || {};
            const misstatementAmount = typeData.misstatement || "0";
            
            return {
              id,
              type,
              workspace: value.workspace || "",
              amount: parseFloat(misstatementAmount) || 0,
            };
          });
          setMisstatements(items);
        }
      }
    } catch (error) {
      console.error("Error loading misstatements:", error);
    }
  };

  useEffect(() => {
    const load = async () => {
      if (!currentEngagement || !user) {
        setUserEngagementRole(null);
        return;
      }
      try {
        const team = await apiService.getEngagementTeam(currentEngagement.id);
        const m = team.find((member: any) => String(member.id) === String(user.id));
        setUserEngagementRole(m?.role ?? null);
      } catch {
        setUserEngagementRole(null);
      }
    };
    load();
  }, [currentEngagement, user]);

  const loadCardSignoffs = useCallback(async () => {
    try {
      const data = await loadMiscSignoffs();
      setCardSignoffs(getSignoffEntriesForCard(WORKSPACE_NAME, WORKSPACE_NAME, data.signoff));
    } catch {
      setCardSignoffs([]);
    }
  }, []);

  useEffect(() => {
    loadCardSignoffs();
  }, [loadCardSignoffs]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-2">
          <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-blue-400"></div>
          <span className="text-white">Loading misstatement data...</span>
        </div>
      </div>
    );
  }

  const totalMisstatementAmount = misstatements.reduce((sum, m) => sum + Math.abs(m.amount), 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 p-6">
      <PlanningSignoffDialog
        isOpen={signoffDialogOpen}
        onClose={() => setSignoffDialogOpen(false)}
        workspaceName={WORKSPACE_NAME}
        cardNames={[WORKSPACE_NAME]}
        user={user ? { user_name: (user as any).user_name ?? (user as any).name, type: (user as any).type } : null}
        userEngagementRole={userEngagementRole}
        onSignoffDone={loadCardSignoffs}
      />
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Evaluate Audit Results Including Misstatements</h1>
            <p className="text-white/60">Comprehensive evaluation of audit results and misstatements for {companyName || "the engagement"}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <PlanningSignoffTokens signoffs={cardSignoffs} className="mt-0" />
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSignoffDialogOpen(true)}
              className="border-white/20 text-white hover:bg-white/10"
            >
              <FileSignature className="h-4 w-4 mr-1.5" />
              Sign off
            </Button>
            {!isEditMode ? (
              <Button
                onClick={() => setIsEditMode(true)}
                variant="outline"
                size="sm"
                className="border-white/20 text-white hover:bg-white/10"
              >
                Edit
              </Button>
            ) : (
              <div className="flex items-center gap-2">
                <Button
                  onClick={handleSave}
                  disabled={isSaving}
                  size="sm"
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  {saveButtonLabel}
                </Button>
                <Button
                  onClick={() => setIsEditMode(false)}
                  variant="outline"
                  size="sm"
                  className="border-white/20 text-white hover:bg-white/10"
                >
                  Stop Editing
                </Button>
              </div>
            )}
          </div>
        </div>
        {saveError && (
          <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400">
            {saveError}
          </div>
        )}

        {/* Misstatement Summary */}
        <Card className="border-white/10 bg-white/5 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-yellow-500/30 to-yellow-600/20 flex items-center justify-center">
                <FileText className="h-5 w-5 text-yellow-400" />
              </div>
              <div>
                <CardTitle className="text-white text-lg">Misstatement Summary</CardTitle>
                <p className="text-xs text-white/50 mt-0.5">Identified Misstatements</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto mb-4">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-white/70">Misstatement ID</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-white/70">Type</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-white/70">Workspace</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-white/70">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {misstatements.length > 0 ? (
                    misstatements.map((mis) => (
                      <tr
                        key={mis.id}
                        className="border-b border-white/5 hover:bg-white/5 cursor-pointer"
                        onClick={() => {
                          if (onSelect) {
                            onSelect("misstatement-library");
                          }
                        }}
                      >
                        <td className="py-3 px-4 text-white">{mis.id}</td>
                        <td className="py-3 px-4 text-white/80">{mis.type}</td>
                        <td className="py-3 px-4 text-white/70">{mis.workspace || "N/A"}</td>
                        <td className="py-3 px-4 text-right text-white">{formatCurrency(mis.amount)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} className="py-8 text-center text-white/40">
                        No misstatements found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {misstatements.length > 0 && (
              <div className="mt-4 p-4 rounded-lg bg-white/5 border border-white/10">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs text-white/60 mb-1">Total Misstatement Amount</p>
                    <p className="text-lg font-bold text-white">{formatCurrency(totalMisstatementAmount)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-white/60 mb-1">Planning Materiality</p>
                    <p className="text-lg font-bold text-white">{formatCurrency(materialityData.planningMateriality)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-white/60 mb-1">Performance Materiality</p>
                    <p className="text-lg font-bold text-white">{formatCurrency(materialityData.performanceMateriality)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-white/60 mb-1">Clearly Trivial Threshold</p>
                    <p className="text-lg font-bold text-white">{formatCurrency(materialityData.clearlyTrivialThreshold)}</p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Questions Section */}
        <Card className="border-white/10 bg-white/5 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-white text-lg">Questions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Question 1 */}
            <Card className="border-white/10 bg-black/40">
              <CardHeader>
                <CardTitle className="text-white text-base">
                  1. Does the materiality remain appropriate in context of entity's actual financial results?
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <RadioGroup
                  value={questionStates["MISST-01"]?.selectedOption || ""}
                  onValueChange={(value) => handleQuestionOptionChange("MISST-01", value)}
                  disabled={!isEditMode}
                  className="flex gap-6"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Yes" id="misst-01-yes" />
                    <Label htmlFor="misst-01-yes" className="text-white cursor-pointer">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="No" id="misst-01-no" />
                    <Label htmlFor="misst-01-no" className="text-white cursor-pointer">No</Label>
                  </div>
                </RadioGroup>
                {questionStates["MISST-01"]?.selectedOption === "No" && (
                  <DocumentManager
                    sectionKey="concluding-evaluate-audit-results"
                    itemId="MISST-01"
                    itemQuestion="Does the materiality remain appropriate in context of entity's actual financial results?"
                    itemTextResponse={questionStates["MISST-01"]?.textResponse || null}
                    itemDocuments={questionStates["MISST-01"]?.documents || []}
                    onDocumentsChange={(docs) => handleQuestionDocumentsChange("MISST-01", docs)}
                    onTextResponseChange={(text) => handleQuestionTextResponseChange("MISST-01", text)}
                    disabled={!isEditMode}
                  />
                )}
              </CardContent>
            </Card>

            {/* Question 2 */}
            <Card className="border-white/10 bg-black/40">
              <CardHeader>
                <CardTitle className="text-white text-base">
                  2. Were any misstatements indicative of fraud?
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <RadioGroup
                  value={questionStates["MISST-02"]?.selectedOption || ""}
                  onValueChange={(value) => handleQuestionOptionChange("MISST-02", value)}
                  disabled={!isEditMode}
                  className="flex gap-6"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Yes" id="misst-02-yes" />
                    <Label htmlFor="misst-02-yes" className="text-white cursor-pointer">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="No" id="misst-02-no" />
                    <Label htmlFor="misst-02-no" className="text-white cursor-pointer">No</Label>
                  </div>
                </RadioGroup>
                {questionStates["MISST-02"]?.selectedOption === "Yes" && (
                  <DocumentManager
                    sectionKey="concluding-evaluate-audit-results"
                    itemId="MISST-02"
                    itemQuestion="Were any misstatements indicative of fraud?"
                    itemTextResponse={questionStates["MISST-02"]?.textResponse || null}
                    itemDocuments={questionStates["MISST-02"]?.documents || []}
                    onDocumentsChange={(docs) => handleQuestionDocumentsChange("MISST-02", docs)}
                    onTextResponseChange={(text) => handleQuestionTextResponseChange("MISST-02", text)}
                    disabled={!isEditMode}
                  />
                )}
              </CardContent>
            </Card>

            {/* Question 3 */}
            <Card className="border-white/10 bg-black/40">
              <CardHeader>
                <CardTitle className="text-white text-base">
                  3. Are there any indicators that there may be greater than acceptably low level of risk that possible undetected misstatements (when taken with the aggregate of misstatements are accumulated during the audit) could be material?
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <RadioGroup
                  value={questionStates["MISST-03"]?.selectedOption || ""}
                  onValueChange={(value) => handleQuestionOptionChange("MISST-03", value)}
                  disabled={!isEditMode}
                  className="flex gap-6"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Yes" id="misst-03-yes" />
                    <Label htmlFor="misst-03-yes" className="text-white cursor-pointer">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="No" id="misst-03-no" />
                    <Label htmlFor="misst-03-no" className="text-white cursor-pointer">No</Label>
                  </div>
                </RadioGroup>
                {questionStates["MISST-03"]?.selectedOption === "Yes" && (
                  <DocumentManager
                    sectionKey="concluding-evaluate-audit-results"
                    itemId="MISST-03"
                    itemQuestion="Are there any indicators that there may be greater than acceptably low level of risk that possible undetected misstatements (when taken with the aggregate of misstatements are accumulated during the audit) could be material?"
                    itemTextResponse={questionStates["MISST-03"]?.textResponse || null}
                    itemDocuments={questionStates["MISST-03"]?.documents || []}
                    onDocumentsChange={(docs) => handleQuestionDocumentsChange("MISST-03", docs)}
                    onTextResponseChange={(text) => handleQuestionTextResponseChange("MISST-03", text)}
                    disabled={!isEditMode}
                  />
                )}
              </CardContent>
            </Card>

            {/* Question 4 */}
            <Card className="border-white/10 bg-black/40">
              <CardHeader>
                <CardTitle className="text-white text-base">
                  4. Have any significant changes been made during the audit to the overall audit strategy and/or audit plan?
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <RadioGroup
                  value={questionStates["MISST-04"]?.selectedOption || ""}
                  onValueChange={(value) => handleQuestionOptionChange("MISST-04", value)}
                  disabled={!isEditMode}
                  className="flex gap-6"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Yes" id="misst-04-yes" />
                    <Label htmlFor="misst-04-yes" className="text-white cursor-pointer">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="No" id="misst-04-no" />
                    <Label htmlFor="misst-04-no" className="text-white cursor-pointer">No</Label>
                  </div>
                </RadioGroup>
                {questionStates["MISST-04"]?.selectedOption === "Yes" && (
                  <DocumentManager
                    sectionKey="concluding-evaluate-audit-results"
                    itemId="MISST-04"
                    itemQuestion="Have any significant changes been made during the audit to the overall audit strategy and/or audit plan?"
                    itemTextResponse={questionStates["MISST-04"]?.textResponse || null}
                    itemDocuments={questionStates["MISST-04"]?.documents || []}
                    onDocumentsChange={(docs) => handleQuestionDocumentsChange("MISST-04", docs)}
                    onTextResponseChange={(text) => handleQuestionTextResponseChange("MISST-04", text)}
                    disabled={!isEditMode}
                  />
                )}
              </CardContent>
            </Card>

            {/* Question 5 */}
            <Card className="border-white/10 bg-black/40">
              <CardHeader>
                <CardTitle className="text-white text-base">
                  5. Has there been a change in the assessed engagement risk during the course of the engagement or a change in our initial decisions regarding engagement continuance?
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <RadioGroup
                  value={questionStates["MISST-05"]?.selectedOption || ""}
                  onValueChange={(value) => handleQuestionOptionChange("MISST-05", value)}
                  disabled={!isEditMode}
                  className="flex gap-6"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Yes" id="misst-05-yes" />
                    <Label htmlFor="misst-05-yes" className="text-white cursor-pointer">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="No" id="misst-05-no" />
                    <Label htmlFor="misst-05-no" className="text-white cursor-pointer">No</Label>
                  </div>
                </RadioGroup>
                {questionStates["MISST-05"]?.selectedOption === "Yes" && (
                  <DocumentManager
                    sectionKey="concluding-evaluate-audit-results"
                    itemId="MISST-05"
                    itemQuestion="Has there been a change in the assessed engagement risk during the course of the engagement or a change in our initial decisions regarding engagement continuance?"
                    itemTextResponse={questionStates["MISST-05"]?.textResponse || null}
                    itemDocuments={questionStates["MISST-05"]?.documents || []}
                    onDocumentsChange={(docs) => handleQuestionDocumentsChange("MISST-05", docs)}
                    onTextResponseChange={(text) => handleQuestionTextResponseChange("MISST-05", text)}
                    disabled={!isEditMode}
                  />
                )}
              </CardContent>
            </Card>

            {/* Question 6 */}
            <Card className="border-white/10 bg-black/40">
              <CardHeader>
                <CardTitle className="text-white text-base">
                  6. Were any additional significant risk identified during the course of the engagement?
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <RadioGroup
                  value={questionStates["MISST-06"]?.selectedOption || ""}
                  onValueChange={(value) => handleQuestionOptionChange("MISST-06", value)}
                  disabled={!isEditMode}
                  className="flex gap-6"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Yes" id="misst-06-yes" />
                    <Label htmlFor="misst-06-yes" className="text-white cursor-pointer">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="No" id="misst-06-no" />
                    <Label htmlFor="misst-06-no" className="text-white cursor-pointer">No</Label>
                  </div>
                </RadioGroup>
                {questionStates["MISST-06"]?.selectedOption === "Yes" && (
                  <DocumentManager
                    sectionKey="concluding-evaluate-audit-results"
                    itemId="MISST-06"
                    itemQuestion="Were any additional significant risk identified during the course of the engagement?"
                    itemTextResponse={questionStates["MISST-06"]?.textResponse || null}
                    itemDocuments={questionStates["MISST-06"]?.documents || []}
                    onDocumentsChange={(docs) => handleQuestionDocumentsChange("MISST-06", docs)}
                    onTextResponseChange={(text) => handleQuestionTextResponseChange("MISST-06", text)}
                    disabled={!isEditMode}
                  />
                )}
              </CardContent>
            </Card>

            {/* Question 7 */}
            <Card className="border-white/10 bg-black/40">
              <CardHeader>
                <CardTitle className="text-white text-base">
                  7. Have we identified information that is inconsistent with our final conclusion regarding a significant matter?
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <RadioGroup
                  value={questionStates["MISST-07"]?.selectedOption || ""}
                  onValueChange={(value) => handleQuestionOptionChange("MISST-07", value)}
                  disabled={!isEditMode}
                  className="flex gap-6"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Yes" id="misst-07-yes" />
                    <Label htmlFor="misst-07-yes" className="text-white cursor-pointer">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="No" id="misst-07-no" />
                    <Label htmlFor="misst-07-no" className="text-white cursor-pointer">No</Label>
                  </div>
                </RadioGroup>
                {questionStates["MISST-07"]?.selectedOption === "Yes" && (
                  <DocumentManager
                    sectionKey="concluding-evaluate-audit-results"
                    itemId="MISST-07"
                    itemQuestion="Have we identified information that is inconsistent with our final conclusion regarding a significant matter?"
                    itemTextResponse={questionStates["MISST-07"]?.textResponse || null}
                    itemDocuments={questionStates["MISST-07"]?.documents || []}
                    onDocumentsChange={(docs) => handleQuestionDocumentsChange("MISST-07", docs)}
                    onTextResponseChange={(text) => handleQuestionTextResponseChange("MISST-07", text)}
                    disabled={!isEditMode}
                  />
                )}
              </CardContent>
            </Card>

            {/* Question 8 - Document only */}
            <Card className="border-white/10 bg-black/40">
              <CardHeader>
                <CardTitle className="text-white text-base">
                  8. Document significant judgements relating to the auditor's determination of whether the accounting estimates and related disclosures are reasonable in the context of applicable financial reporting framework or are misstated.
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <DocumentManager
                  sectionKey="concluding-evaluate-audit-results"
                  itemId="MISST-08"
                  itemQuestion="Document significant judgements relating to the auditor's determination of whether the accounting estimates and related disclosures are reasonable in the context of applicable financial reporting framework or are misstated."
                  itemTextResponse={questionStates["MISST-08"]?.textResponse || null}
                  itemDocuments={questionStates["MISST-08"]?.documents || []}
                  onDocumentsChange={(docs) => handleQuestionDocumentsChange("MISST-08", docs)}
                  onTextResponseChange={(text) => handleQuestionTextResponseChange("MISST-08", text)}
                  disabled={!isEditMode}
                />
              </CardContent>
            </Card>

            {/* Question 9 */}
            <Card className="border-white/10 bg-black/40">
              <CardHeader>
                <CardTitle className="text-white text-base">
                  9. Has sufficient appropriate audit evidence been obtained for all testing performed?
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <RadioGroup
                  value={questionStates["MISST-09"]?.selectedOption || ""}
                  onValueChange={(value) => handleQuestionOptionChange("MISST-09", value)}
                  disabled={!isEditMode}
                  className="flex gap-6"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Yes" id="misst-09-yes" />
                    <Label htmlFor="misst-09-yes" className="text-white cursor-pointer">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="No" id="misst-09-no" />
                    <Label htmlFor="misst-09-no" className="text-white cursor-pointer">No</Label>
                  </div>
                </RadioGroup>
                {questionStates["MISST-09"]?.selectedOption === "No" && (
                  <DocumentManager
                    sectionKey="concluding-evaluate-audit-results"
                    itemId="MISST-09"
                    itemQuestion="Has sufficient appropriate audit evidence been obtained for all testing performed?"
                    itemTextResponse={questionStates["MISST-09"]?.textResponse || null}
                    itemDocuments={questionStates["MISST-09"]?.documents || []}
                    onDocumentsChange={(docs) => handleQuestionDocumentsChange("MISST-09", docs)}
                    onTextResponseChange={(text) => handleQuestionTextResponseChange("MISST-09", text)}
                    disabled={!isEditMode}
                  />
                )}
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

